import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { makeStyles, useTheme } from "@material-ui/styles";
import {
	Card,
	CardContent,
	Typography,
	Grid,
	CircularProgress,
	Button,
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	TextField,
	Table,
	TableHead,
	TableBody,
	TableRow,
	TableCell,
	Chip,
	Paper,
	useMediaQuery,
} from "@material-ui/core";
import WarningIcon from "@material-ui/icons/Warning";
import { ScrollToTopOnMount, PopupSnackbar } from "frame/components";
import {
	formatDate,
	useLoadedState,
	isStaffUser,
	isSuperAdminUser,
	useRouter,
	useCurrentUser,
	useCurrentOrganisation,
	dayDifference,
} from "utils";
import * as fromRoutes from "../../actions/types";
import { getWebsiteBaseUrl } from "frame";
import { Elements, StripeProvider } from "react-stripe-elements";
import AddPaymentModal from "./add-payment-form";
import SubscriptionModal from "./subscription-modal";
import { GracePeriodBanner } from "./grace-period-warning";
import { stripeHelper } from "utils/stripeHelper";
import { safetyWiseApi } from "utils/safetyWiseApi";

const useStyles = makeStyles((theme) => ({
	root: {
		padding: theme.spacing(2, 3, 10, 3),
		backgroundColor: theme.palette.white,
		borderRadius: theme.shape.borderRadius,
		boxShadow: theme.shadows[1],
		marginBottom: theme.spacing(2),
	},
	cardRoot: {
		position: "relative",
		padding: theme.spacing(2),
		marginBottom: theme.spacing(2),
	},
	content: {
		padding: theme.spacing(2, 2),
		flexGrow: 1,
		display: "flex",
		alignItems: "center",
		justifyContent: "space-between",
		[theme.breakpoints.down("sm")]: {
			width: "100%",
			flexWrap: "wrap",
		},
		"&:last-child": {
			paddingBottom: theme.spacing(2),
		},
	},
	label: {
		padding: theme.spacing(0, 0, 0.5, 0),
		color: "#999999",
	},
	value: {
		padding: theme.spacing(0, 0, 0.5, 0),
	},
	body: {
		margin: theme.spacing(1, 0, 2, 0),
		maxWidth: 800,
	},
	card: {
		margin: theme.spacing(6, 0, 2, 0),
	},
	icon: {
		width: 20,
		height: 20,
	},
	supportTitle: {
		marginTop: theme.spacing(5),
	},
	supportContact: {
		marginTop: theme.spacing(2),
	},
	link: {
		color: theme.palette.text.link,
	},
	error: {
		backgroundColor: theme.palette.error.main,
		color: theme.palette.white,
	},
	warningText: {
		color: theme.palette.error.main,
	},
	button: {
		padding: theme.spacing(1, 4),
		color: theme.palette.brand,
		background: theme.palette.highlight,
		borderColor: theme.palette.highlight,
		fontWeight: 600,
		"&:hover": {
			background: "#c1c72c",
			borderColor: "#c1c72c",
		},
		[theme.breakpoints.down("xs")]: {
			padding: theme.spacing(1, 2),
		},
	},
	buttonContainer: {
		display: "flex",
		gap: theme.spacing(2),
		marginTop: theme.spacing(3),
		marginBottom: theme.spacing(2),
		[theme.breakpoints.down("xs")]: {
			flexDirection: "column",
			gap: theme.spacing(1),
		},
	},
	buttonOutlined: {
		padding: theme.spacing(1, 4),
		color: theme.palette.brand,
		borderColor: theme.palette.brand,
		fontWeight: 600,
		"&:hover": {
			borderColor: theme.palette.brand,
			background: "rgba(0, 0, 0, 0.04)",
		},
		[theme.breakpoints.down("xs")]: {
			padding: theme.spacing(1, 2),
		},
	},
	addOnCard: {
		marginTop: theme.spacing(3),
	},
	addOnItem: {
		padding: theme.spacing(2, 0),
		borderBottom: `1px solid ${theme.palette.divider}`,
		"&:last-child": {
			borderBottom: "none",
		},
	},
	addOnTitle: {
		fontWeight: 600,
		marginBottom: theme.spacing(0.5),
	},
	addOnPrice: {
		color: theme.palette.text.secondary,
		marginBottom: theme.spacing(1),
	},
	billingSummaryCard: {
		marginTop: theme.spacing(3),
		backgroundColor: theme.palette.grey[50],
	},
	billingSummaryTitle: {
		fontWeight: 600,
		marginBottom: theme.spacing(2),
	},
	billingSummaryRow: {
		display: "flex",
		justifyContent: "space-between",
		alignItems: "center",
		padding: theme.spacing(1, 0),
		borderBottom: `1px solid ${theme.palette.divider}`,
		"&:last-child": {
			borderBottom: "none",
			borderTop: `2px solid ${theme.palette.divider}`,
			marginTop: theme.spacing(1),
			paddingTop: theme.spacing(2),
			fontWeight: 600,
		},
	},
	billingSummaryLabel: {
		color: theme.palette.text.secondary,
	},
	billingSummaryValue: {
		fontWeight: 500,
	},
	billingSummaryTotal: {
		fontSize: "1.1rem",
	},
	transactionCard: {
		marginTop: theme.spacing(3),
	},
	transactionTitle: {
		fontWeight: 600,
		marginBottom: theme.spacing(2),
	},
	statusChipPaid: {
		backgroundColor: "#d4edda",
		color: "#155724",
		fontSize: "0.75rem",
	},
	statusChipFailed: {
		backgroundColor: "#f8d7da",
		color: "#721c24",
		fontSize: "0.75rem",
	},
	statusChipOther: {
		backgroundColor: "#e2e3e5",
		color: "#383d41",
		fontSize: "0.75rem",
	},
	txMobileCard: {
		padding: theme.spacing(1.5),
		marginBottom: theme.spacing(1.5),
		borderRadius: 8,
		border: "1px solid #e0e0e0",
		backgroundColor: "#fafafa",
	},
	txMobileRow: {
		display: "flex",
		justifyContent: "space-between",
		alignItems: "center",
		marginBottom: theme.spacing(0.5),
	},
	txMobileLabel: {
		fontSize: "0.72rem",
		color: "#888",
		textTransform: "uppercase",
		letterSpacing: "0.05em",
	},
	txMobileValue: {
		fontSize: "0.9rem",
		fontWeight: 500,
	},
	txMobileDescription: {
		fontSize: "0.85rem",
		color: "#444",
		marginBottom: theme.spacing(0.75),
	},
	txMobileAmount: {
		fontSize: "1rem",
		fontWeight: 700,
	},
	txMobileViewLink: {
		fontSize: "0.8rem",
		color: theme.palette.primary.main,
		textDecoration: "none",
		"&:hover": { textDecoration: "underline" },
	},
}));

const apiKey = stripeHelper.getApiKey();

/* eslint-disable */
const Subscription = ({
	subscription,
	role,
	onFetchSubscription,
	onOpenAddPaymentModal,
	onCloseAddPaymentModal,
	onCancelSubscription,
	onExtendRequest,
	onExtendSuccess,
	onExtendFailed,
	currentOrganisationGUID,
}) => {
	const classes = useStyles();
	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
	const currentUser = useCurrentUser();
	const currentOrganisationId = useCurrentOrganisation();
	const [openDeleteModal, setOpenDeleteModal] = useLoadedState(false);
	const { history } = useRouter();
	const [buttonDisabled, setButtonDisabled] = useState(false);
	const [errorMessage, setErrorMessage] = useState("");
	const [successMessage, setSuccessMessage] = useState("");
	const [extendDialogOpen, setExtendDialogOpen] = useState(false);
	const [extendDays, setExtendDays] = useState(7);

	const isSuperAdmin = isSuperAdminUser(role);

	if (isStaffUser(role)) {
		history.push(fromRoutes.ACCOUNT_PROFILE_PATH);
	}

	const handleApiError = (error) => {
		if (error.response && error.response.status === "401") {
			setErrorMessage("You are not authorized to perform this action.");
		} else if (error.response && error.response.status === "404") {
			setErrorMessage("The record no longer exists.");
		} else {
			setErrorMessage(error.response?.data?.message || "An error occurred. Please try again.");
		}
	};

	const handleEnableEquipment = () => {
		setButtonDisabled(true);
		setErrorMessage("");
		safetyWiseApi
			.post(`${currentOrganisationGUID}/organisation/subscription/addon/equipment/enable`)
			.then(() => {
				onFetchSubscription();
				setButtonDisabled(false);
				setSuccessMessage("Equipment add-on enabled successfully.");
			})
			.catch((error) => {
				setButtonDisabled(false);
				handleApiError(error);
			});
	};

	const handleDisableEquipment = () => {
		setButtonDisabled(true);
		setErrorMessage("");
		safetyWiseApi
			.post(`${currentOrganisationGUID}/organisation/subscription/addon/equipment/disable`)
			.then(() => {
				// Add a small delay to ensure Stripe has processed the change
				setTimeout(() => {
					onFetchSubscription();
					setButtonDisabled(false);
					setSuccessMessage("Equipment add-on disabled successfully.");
				}, 500);
			})
			.catch((error) => {
				setButtonDisabled(false);
				handleApiError(error);
			});
	};

	useEffect(() => {
		onCloseAddPaymentModal(); // ensure modal is closed on subscription page load
		onFetchSubscription();
	}, []);

	const handleExtendAccess = () => {
		onExtendRequest();
		safetyWiseApi
			.post(`${currentOrganisationGUID}/organisation/subscription/extend`, { extendDays })
			.then(() => {
				onExtendSuccess();
				setExtendDialogOpen(false);
				setSuccessMessage(`Access extended by ${extendDays} days.`);
				onFetchSubscription();
			})
			.catch((error) => {
				onExtendFailed(error);
				handleApiError(error);
			});
	};

	const getTransactionChipClass = (status) => {
		if (status === "paid") return classes.statusChipPaid;
		if (status === "open" || status === "uncollectible") return classes.statusChipFailed;
		return classes.statusChipOther;
	};

	const formatter = new Intl.NumberFormat("en-US", {
		style: "currency",
		currency: "NZD",
	});

	return (
		<div className={classes.root}>
			<ScrollToTopOnMount />
			{subscription.flags.fetch !== "processing" &&
			currentUser.organisation &&
			subscription ? (
				<>
					<div className={classes.body}>
					{/* Grace period / disabled warning banner */}
					<GracePeriodBanner
						subscriptionStatus={subscription.subscriptionStatus}
						daysRemaining={subscription.warningDaysRemaining}
						gracePeriodEnd={subscription.gracePeriodEndDate}
						onExtendClick={
							isSuperAdmin &&
							(subscription.subscriptionStatus === "GracePeriod" || subscription.subscriptionStatus === "Disabled")
								? () => setExtendDialogOpen(true)
								: undefined
						}
					/>
						<Card className={classes.cardRoot}>
							<CardContent className={classes.content}>
								<Grid
									className={classes.container}
									container
									spacing={2}
								>
									<Grid item md={6} sm={6} xs={12}>
										<Typography
											className={classes.label}
											variant="body2"
										>
											Organisation Name
										</Typography>
										<Typography variant="h5">
											{currentUser.organisation}
										</Typography>
									</Grid>

									<Grid item md={6} sm={6} xs={12}>
										<Typography
											className={classes.label}
											variant="body2"
										>
											Number of Active Users
										</Typography>
										<Typography variant="h5">
											{subscription.numberOfUsers ?? "-"}
										</Typography>
									</Grid>

									<Grid item md={6} sm={6} xs={12}>
										<Typography
											className={classes.label}
											variant="body2"
										>
											Next Billing Amount (excl gst)
										</Typography>
										<Typography variant="h5">
											{subscription.nextBillingAmount
												? formatter.format(
														subscription.nextBillingAmount
												  )
												: "-"}
										</Typography>
										{subscription.baseSubscriptionAmount > 0 && subscription.totalAddOnAmount > 0 && (
											<Typography variant="caption" style={{ color: "#666", display: "block", marginTop: 4 }}>
												Base: {formatter.format(subscription.baseSubscriptionAmount)}
												{subscription.totalAddOnAmount > 0 && ` + Add-ons: ${formatter.format(subscription.totalAddOnAmount)}`}
											</Typography>
										)}
									</Grid>

									<Grid item md={6} sm={6} xs={12}>
										<Typography
											className={classes.label}
											variant="body2"
										>
											{subscription.isTrial
												? "Free Trial End Date"
												: subscription.cancelAtDate
												? "Subscription Expiry Date"
												: "Next Billing Date"}
										</Typography>
										<Typography
											className={classes.value}
											variant="h5"
										>
											{subscription.status == "canceled"
												? formatDate(
														subscription.canceledAtDate
												  )
												: formatDate(
														subscription.nextBillingDate
												  )}
										</Typography>
									</Grid>

									{/* <Grid item md={6} sm={6} xs={12}>
										<Typography
											className={classes.label}
											variant="body2"
										>
											Next Billing Date
										</Typography>
										<Typography
											className={classes.value}
											variant="h5"
										>
											<span
												className={classes.warningText}
											>
												{`${subscription.daysRemaining} Days Left `}
											</span>
											{subscription.daysRemaining < 0 && (
												<Chip
													className={classes.error}
													size="small"
													label="Expired"
												/>
											)}
										</Typography>
									</Grid> */}

									<Grid item md={6} sm={6} xs={12}>
										<Typography
											className={classes.label}
											variant="body2"
										>
											Credit Card
										</Typography>
										<Typography
											className={classes.value}
											variant="h5"
										>
											{subscription.last4
												? `**** **** **** ${subscription.last4} `
												: "-"}
										</Typography>
									</Grid>

									<Grid item md={6} sm={6} xs={12}>
										<Typography
											className={classes.label}
											variant="body2"
										>
											Credit Card Expiry Date
										</Typography>
										<Typography
											className={classes.value}
											variant="h5"
										>
											{subscription.expiryYear &&
											subscription.expiryMonth
												? `${subscription.expiryMonth} / ${subscription.expiryYear}`
												: "-"}
										</Typography>
									</Grid>

									<Grid item md={6} sm={6} xs={12}>
										<Typography
											className={classes.label}
											variant="body2"
										>
											Credit Card Type
										</Typography>
										<Typography
											className={classes.value}
											variant="h5"
										>
											{subscription.brand ? (
												<>
													{`${subscription.brand} `}
													{/* <svg {...getSvgProps({ type: subscription.brand.toLowerCase(), images })} /> */}
												</>
											) : (
												"-"
											)}
										</Typography>
									</Grid>

									<Grid item md={6} sm={6} xs={12}>
										<Typography
											className={classes.label}
											variant="body2"
										>
											Subscription Status
										</Typography>
										<Typography
											className={classes.value}
											variant="h5"
										>
											{subscription.isTrial
												? "Free Trial"
												: subscription.status ==
												  "canceled"
												? "Cancelled"
												: subscription.status ==
												  "active"
												? "Active"
												: subscription.status ==
												  "inactive"
												? "Inactive"
												: "No valid payment method"}
											{subscription.isTrial ||
											(subscription.status == "active" &&
												subscription.cancelAtDate)
												? ` (${dayDifference(
														undefined,
														subscription.nextBillingDate
												  )} Days Remaining)`
												: null}
										</Typography>
									</Grid>
								</Grid>
							</CardContent>
						</Card>

						{/* Add-ons Section */}
						<Card className={`${classes.cardRoot} ${classes.addOnCard}`}>
							<CardContent style={{ padding: 16 }}>
								<Typography variant="h5" style={{ marginBottom: 16 }}>
									Available Add-ons
								</Typography>
								
								{/* Equipment Add-on */}
								<div className={classes.addOnItem}>
									<Grid container spacing={2} alignItems="center">
										<Grid item xs={12} md={6}>
											<Typography variant="h6" className={classes.addOnTitle}>
												Equipment{subscription.addOns?.find(addon => addon.featureCode === "EQUIPMENT")?.enabled && (
													<span style={{ color: "#4caf50", fontWeight: "normal" }}> - enabled</span>
												)}
											</Typography>
											{subscription.numberOfUsers > 0 && (
												<Typography variant="body2" className={classes.addOnPrice}>
													{subscription.numberOfUsers} users × $2.00 = {formatter.format(subscription.numberOfUsers * 2.00)}/month
												</Typography>
											)}
										</Grid>
										<Grid item xs={12} md={6} style={{ textAlign: "right" }}>
											{subscription.addOns?.find(addon => addon.featureCode === "EQUIPMENT")?.enabled ? (
												<Button
													variant="outlined"
													color="secondary"
													onClick={handleDisableEquipment}
													disabled={buttonDisabled || subscription.status === "inactive"}
												>
													Disable
												</Button>
											) : (
												<Button
													variant="contained"
													color="primary"
													onClick={handleEnableEquipment}
													disabled={buttonDisabled || subscription.status === "inactive"}
												>
													Enable
												</Button>
											)}
										</Grid>
									</Grid>
								</div>
							</CardContent>
						</Card>

						{/* Billing Summary Section */}
						{subscription && (
							<Card className={`${classes.cardRoot} ${classes.billingSummaryCard}`}>
								<CardContent style={{ padding: 16 }}>
									<Typography variant="h5" className={classes.billingSummaryTitle}>
										Billing Summary
									</Typography>
									
									<div className={classes.billingSummaryRow}>
										<div>
											<Typography variant="body1" className={classes.billingSummaryLabel}>
												Subscription ({subscription.numberOfUsers || 0} active users)
											</Typography>
										</div>
										<Typography variant="body1" className={classes.billingSummaryValue}>
											{subscription.baseSubscriptionAmount > 0 
												? formatter.format(subscription.baseSubscriptionAmount)
												: subscription.nextBillingAmount > 0 
													? formatter.format(subscription.nextBillingAmount)
													: "-"}
										</Typography>
									</div>
									
									{subscription.totalAddOnAmount > 0 && subscription.addOns && subscription.addOns.length > 0 && (
										<div className={classes.billingSummaryRow}>
											<div>
												<Typography variant="body1" className={classes.billingSummaryLabel}>
													Add-ons ({subscription.numberOfUsers || 0} active users)
												</Typography>
												{subscription.addOns.filter(addon => addon.enabled).map((addon, index) => (
													<Typography key={index} variant="caption" style={{ display: "block", marginTop: 4, color: "#666" }}>
														• {addon.name}: {formatter.format(addon.totalPrice)}
													</Typography>
												))}
											</div>
											<Typography variant="body1" className={classes.billingSummaryValue}>
												{formatter.format(subscription.totalAddOnAmount)}
											</Typography>
										</div>
									)}
									
									<div className={classes.billingSummaryRow}>
										<Typography variant="body1" className={classes.billingSummaryLabel}>
											Next Billing Amount (excl gst)
										</Typography>
										<Typography variant="body1" className={`${classes.billingSummaryValue} ${classes.billingSummaryTotal}`}>
											{subscription.nextBillingAmount > 0 
												? formatter.format(subscription.nextBillingAmount)
												: "-"}
										</Typography>
									</div>
								</CardContent>
							</Card>
						)}

					{/* Transaction History Section */}
					<Card className={`${classes.cardRoot} ${classes.transactionCard}`}>
						<CardContent style={{ padding: 16 }}>
							<Typography variant="h5" className={classes.transactionTitle}>
								Recent Transactions
							</Typography>
							{subscription.recentTransactions && subscription.recentTransactions.length > 0 ? (
								isMobile ? (
									<div>
										{subscription.recentTransactions.map((tx, i) => (
											<div key={i} className={classes.txMobileCard}>
												<div className={classes.txMobileRow}>
													<span className={classes.txMobileAmount}>{formatter.format(tx.amount)}</span>
													<Chip
														label={tx.status.charAt(0).toUpperCase() + tx.status.slice(1)}
														size="small"
														className={getTransactionChipClass(tx.status)}
													/>
												</div>
												<Typography className={classes.txMobileDescription}>
													{tx.description}
												</Typography>
												<div className={classes.txMobileRow}>
													<span className={classes.txMobileLabel}>{formatDate(tx.date)}</span>
													{tx.invoiceUrl && (
														<a href={tx.invoiceUrl} target="_blank" rel="noopener noreferrer" className={classes.txMobileViewLink}>
															View Invoice
														</a>
													)}
												</div>
											</div>
										))}
									</div>
								) : (
									<Table size="small">
										<TableHead>
											<TableRow>
												<TableCell><strong>Date</strong></TableCell>
												<TableCell><strong>Description</strong></TableCell>
												<TableCell><strong>Amount</strong></TableCell>
												<TableCell><strong>Status</strong></TableCell>
												<TableCell></TableCell>
											</TableRow>
										</TableHead>
										<TableBody>
											{subscription.recentTransactions.map((tx, i) => (
												<TableRow key={i}>
													<TableCell>{formatDate(tx.date)}</TableCell>
													<TableCell>{tx.description}</TableCell>
													<TableCell>{formatter.format(tx.amount)}</TableCell>
													<TableCell>
														<Chip
															label={tx.status.charAt(0).toUpperCase() + tx.status.slice(1)}
															size="small"
															className={getTransactionChipClass(tx.status)}
														/>
													</TableCell>
													<TableCell>
														{tx.invoiceUrl && (
															<a href={tx.invoiceUrl} target="_blank" rel="noopener noreferrer" style={{ fontSize: "0.8rem" }}>
																View
															</a>
														)}
													</TableCell>
												</TableRow>
											))}
										</TableBody>
									</Table>
								)
							) : (
								<Typography variant="body2" style={{ color: "#999" }}>
									No transaction history available.
								</Typography>
							)}
						</CardContent>
				</Card>
					</div>
					{subscription.status == "inactive" ? (
						<div className={classes.buttonContainer}>
							<Button
								className={classes.buttonOutlined}
								variant="outlined"
								onClick={onOpenAddPaymentModal}
								disabled={buttonDisabled}
							>
								Add Payment Method
							</Button>
							<Button
								className={classes.button}
								variant="contained"
								onClick={() => {
									setButtonDisabled(true);
									safetyWiseApi
										.post(`re-subscribe/${currentOrganisationGUID}`)
										.then(() => {
											onFetchSubscription();
											setButtonDisabled(false);
											setSuccessMessage(
												"You have successfully reactivated your subscription."
											);
										})
										.catch(handleApiError);
								}}
								disabled={buttonDisabled}
							>
								Reactivate Subscription
							</Button>
						</div>
					) : subscription.cancelAtDate || subscription.canceledAtDate ? (
						<div className={classes.buttonContainer}>
							<Button
								className={classes.buttonOutlined}
								variant="outlined"
								onClick={onOpenAddPaymentModal}
								disabled={buttonDisabled}
							>
								UPDATE CREDIT CARD
							</Button>
							{Date.now() <
							Date.parse(
								subscription.cancelAtDate || subscription.canceledAtDate
							) ? (
								<Button
									className={classes.button}
									variant="contained"
									onClick={() => {
										setButtonDisabled(true);
										safetyWiseApi
											.post(
												`update-subscription-cancellation/${currentOrganisationGUID}`,
												{
													cancelAtPeriodEnd: false,
												}
											)
											.then(() => {
												onFetchSubscription();
												setButtonDisabled(false);
												setSuccessMessage(
													"Your subscription has been activated."
												);
											})
											.catch(handleApiError);
									}}
									disabled={buttonDisabled}
								>
									Don't Cancel
								</Button>
							) : (
								<Button
									className={classes.button}
									variant="contained"
									onClick={() => {
										setButtonDisabled(true);
										safetyWiseApi
											.post(`re-subscribe/${currentOrganisationGUID}`)
											.then(() => {
												onFetchSubscription();
												setButtonDisabled(false);
												setSuccessMessage(
													"Your have sucessfully re-subscribed."
												);
											})
											.catch(handleApiError);
									}}
									disabled={buttonDisabled}
								>
									Re-Subscribe
								</Button>
							)}
						</div>
					) : (
						<div className={classes.buttonContainer}>
							<Button
								className={classes.buttonOutlined}
								variant="outlined"
								onClick={onOpenAddPaymentModal}
								disabled={buttonDisabled}
							>
								UPDATE CREDIT CARD
							</Button>
							<Button
								className={classes.button}
								variant="contained"
								onClick={() => {
									setButtonDisabled(true);
									safetyWiseApi
										.post(
											`update-subscription-cancellation/${currentOrganisationGUID}`,
											{
												cancelAtPeriodEnd: true,
											}
										)
										.then(() => {
											onFetchSubscription();
											setButtonDisabled(false);
											setSuccessMessage(
												"Your subscription has been terminated."
											);
										})
										.catch(handleApiError);
								}}
								disabled={buttonDisabled}
							>
								Cancel Subscription
							</Button>
						</div>
					)}
					<PopupSnackbar
						message={errorMessage}
						coloured
						error
						onExited={() => setErrorMessage("")}
					/>
					<PopupSnackbar
						message={successMessage}
						coloured
						success
						onExited={() => setSuccessMessage("")}
					/>
				</>
			) : (
				<div
					style={{
						display: "flex",
						alignItems: "center",
						justifyContent: "center",
						width: "100%",
						minHeight: 320,
					}}
				>
					<CircularProgress />
				</div>
			)}

			<div>
				<Typography className={classes.supportTitle} variant="h4">
					Subscription support <br />
				</Typography>
				<Typography className={classes.supportContact} variant="body1">
					Click{" "}
					<a
						className={classes.link}
						href={`${getWebsiteBaseUrl()}/try-buy/`}
						target="_blank"
					>
						here
					</a>{" "}
					to see how subscription costs are calculated. <br />
					Email the support team at{" "}
					<a
						className={classes.link}
						href="mailto:info@fortherecord.pro"
					>
						info@fortherecord.pro
					</a>{" "}
					for further assistance.
				</Typography>
			</div>

			{/* Confirm delete modal */}
			<SubscriptionModal
				open={openDeleteModal}
				setOpen={setOpenDeleteModal}
				onDelete={() => onCancelSubscription(currentOrganisationId)}
			/>

			{/* Add payment modal */}
			<StripeProvider apiKey={apiKey}>
				<Elements>
					<AddPaymentModal
						setSuccessMessage={(message) =>
							setSuccessMessage(message)
						}
						setErrorMessage={(message) => setErrorMessage(message)}
						isUpdate={!!subscription.last4}
					/>
				</Elements>
			</StripeProvider>

			{/* Super Admin: Extend access dialog */}
			<Dialog open={extendDialogOpen} onClose={() => setExtendDialogOpen(false)} maxWidth="xs" fullWidth>
				<DialogTitle>Extend Account Access</DialogTitle>
				<DialogContent>
					<Typography variant="body2" gutterBottom>
						Extend the grace period for this account, giving the administrator more time to update their payment details.
					</Typography>
					<TextField
						label="Number of days"
						type="number"
						value={extendDays}
						onChange={(e) => setExtendDays(Math.max(1, Math.min(90, parseInt(e.target.value) || 1)))}
						inputProps={{ min: 1, max: 90 }}
						fullWidth
						style={{ marginTop: 16 }}
					/>
				</DialogContent>
				<DialogActions>
					<Button onClick={() => setExtendDialogOpen(false)}>Cancel</Button>
					<Button onClick={handleExtendAccess} color="primary" variant="contained"
						disabled={subscription.flags.extend === "processing"}>
						Extend Access
					</Button>
				</DialogActions>
			</Dialog>
			<PopupSnackbar
				message={errorMessage}
				coloured
				error
				onExited={() => setErrorMessage("")}
			/>
			<PopupSnackbar
				message={successMessage}
				coloured
				success
				onExited={() => setSuccessMessage("")}
			/>
		</div>
	);
};
/* eslint-enable */

Subscription.propTypes = {
	subscription: PropTypes.object.isRequired,
	role: PropTypes.string.isRequired,
	staffList: PropTypes.array.isRequired,
	onOpenAddPaymentModal: PropTypes.func.isRequired,
	onCloseAddPaymentModal: PropTypes.func.isRequired,
	onCancelSubscription: PropTypes.func.isRequired,
	onExtendRequest: PropTypes.func.isRequired,
	onExtendSuccess: PropTypes.func.isRequired,
	onExtendFailed: PropTypes.func.isRequired,
};

export default Subscription;
